var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useEffect, useMemo } from "react";
import * as ReactDOM from "react-dom";
import classNames from "classnames";
import "@gouvfr/dsfr/dist/component/modal/modal.css";
import { Col, Container, Row, Button } from "../../index";
import { ModalV2Content, ModalV2Footer } from "./index";
import { getFocusableElements } from "../../../common/focusableFunctions";
var ModalV2 = forwardRef(function (_a, ref) {
    var id = _a.id, idModalTitle = _a.idModalTitle, children = _a.children, onClose = _a.onClose, size = _a.size, className = _a.className, style = _a.style, otherProps = __rest(_a, ["id", "idModalTitle", "children", "onClose", "size", "className", "style"]);
    var modalBodyRef = React.useRef(null);
    var handleKeyDown = function (keydown) {
        if (keydown.code === "Escape") {
            close();
        }
        else if (keydown.code === "Tab") {
            var focusablesElements = getFocusableElements(modalBodyRef);
            if (focusablesElements.length === 0)
                return;
            var firstElement = focusablesElements[0];
            var lastElement = focusablesElements[focusablesElements.length - 1];
            if (!keydown.shiftKey && document.activeElement === lastElement) {
                keydown.preventDefault();
                firstElement.focus();
            }
            else if (keydown.shiftKey && document.activeElement === firstElement) {
                keydown.preventDefault();
                lastElement.focus();
            }
        }
    };
    var onClickOutside = function (event) {
        if (modalBodyRef && modalBodyRef.current && !modalBodyRef.current.contains(event.target)) {
            close();
        }
    };
    var close = function () {
        document.documentElement.removeAttribute("data-fr-scrolling");
        if (onClose)
            onClose();
    };
    useEffect(function () {
        function lockScroll() {
            var scrollY = window.scrollY;
            var scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.setAttribute("data-fr-scrolling", "false");
            document.body.style.top = "-".concat(scrollY, "px");
            var behavior = getComputedStyle(document.documentElement).getPropertyValue("scroll-behavior");
            if (behavior === "smooth")
                document.documentElement.style.scrollBehavior = "auto";
            if (scrollBarGap > 0) {
                document.documentElement.style.setProperty("--scrollbar-width", "".concat(scrollBarGap, "px"));
            }
            return { scrollY: scrollY, behavior: behavior };
        }
        var _a = lockScroll(), scrollY = _a.scrollY, behavior = _a.behavior;
        /*
        Si la modale est ouverte via une action externe,
        on ajoute les contrôles pour la fermeture via échap ou un clique en dehors de la modale.
        on désactive le scroll sur la page.
        */
        window.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", onClickOutside);
        return function unmount() {
            function unlockScroll() {
                document.documentElement.removeAttribute("data-fr-scrolling");
                document.body.style.top = "";
                window.scrollTo(0, scrollY);
                if (behavior === "smooth")
                    document.documentElement.style.removeProperty("scroll-behavior");
                document.documentElement.style.removeProperty("--scrollbar-width");
            }
            unlockScroll();
            /*
              A la fermeture de la modale,
              on enlève les contrôles pour la fermeture via échap ou un clique en dehors de la modale.
               */
            window.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", onClickOutside);
        };
    }, [modalBodyRef, onClose]);
    var modalContent = null;
    var modalFooter = null;
    var recupererElement = function (child) {
        switch (child.type) {
            case ModalV2Content:
                modalContent = child;
                break;
            case ModalV2Footer:
                modalFooter = child;
                break;
        }
    };
    if (Array.isArray(children)) {
        children.forEach(function (child) {
            recupererElement(child);
        });
    }
    else if (children) {
        recupererElement(children);
    }
    var colSize = useMemo(function () {
        switch (size) {
            case "lg":
                return { col: 12, md: 10, lg: 8 };
            case "sm":
                return { col: 12, md: 6, lg: 4 };
            default:
                return { col: 12, md: 8, lg: 6 };
        }
    }, [size]);
    return ReactDOM.createPortal(React.createElement("dialog", __assign({}, otherProps, { "aria-labelledby": idModalTitle, ref: ref, id: id, className: classNames("fr-modal fr-modal--opened", className), style: style, role: "dialog" }),
        React.createElement(Container, { fluid: true, size: "md" },
            React.createElement(Row, { horizontalAlign: "center" },
                React.createElement(Col, __assign({}, colSize, { "data-testid": "modal-dialog" }),
                    React.createElement("div", { className: "fr-modal__body", ref: modalBodyRef },
                        React.createElement("div", { className: "fr-modal__header" },
                            React.createElement(Button, { className: "fr-btn--close", autoFocus: true, "aria-controls": id, onClick: onClose }, "Fermer")),
                        modalContent,
                        modalFooter))))), document.body);
});
export default ModalV2;
